import { useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle2, RefreshCw, XCircle } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { fetchSeoAudit, SEO_STR as S, type SeoAuditReport, type SeoAuditCheck } from './seoApi'

/**
 * A12.26 — SEO-audit hisoboti. Tashqi krauler EMAS: audit adminning o'zi
 * boshqaradigan REAL jadvallardan hisoblanadi (seo_redirects / seo_not_found /
 * seo_config / robots) — `GET /admin/seo/audit`. Har tekshiruv holat (ok / warn /
 * error) + soni bilan; sog'lomlik bali xatolar soniga qarab tushadi.
 */
function sevMeta(sev: SeoAuditCheck['severity']) {
  switch (sev) {
    case 'error':
      return { icon: <XCircle size={15} />, label: S.sevError, cls: 'text-red-500' }
    case 'warn':
      return { icon: <AlertTriangle size={15} />, label: S.sevWarn, cls: 'text-amber-500' }
    default:
      return { icon: <CheckCircle2 size={15} />, label: S.sevOk, cls: 'text-emerald-500' }
  }
}

export function SeoAuditScreen() {
  const [report, setReport] = useState<SeoAuditReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    setLoading(true)
    fetchSeoAudit(ac.signal)
      .then(setReport)
      .finally(() => setLoading(false))
    return () => ac.abort()
  }, [nonce])

  const scoreCls =
    report == null
      ? 'text-primary'
      : report.score >= 80
        ? 'text-emerald-500'
        : report.score >= 50
          ? 'text-amber-500'
          : 'text-red-500'

  return (
    <>
      <PageHeader
        title={S.seoAudit}
        screenId="A12.26"
        actions={
          <button
            type="button"
            onClick={() => setNonce((n) => n + 1)}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary disabled:opacity-50"
          >
            <RefreshCw size={13} />
            {S.auditRun}
          </button>
        }
      />
      <div className="min-h-0 flex-1 space-y-3 overflow-auto p-4">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <div className="flex flex-col gap-1 rounded-lg border border-border-default bg-surface-2 p-4">
            <span className="text-xs text-tertiary">{S.auditScore}</span>
            <span className={`text-3xl font-semibold ${scoreCls}`}>
              {loading || report == null ? '—' : `${report.score}/100`}
            </span>
          </div>
          <div className="flex flex-col gap-1 rounded-lg border border-border-default bg-surface-2 p-4">
            <span className="text-xs text-tertiary">{S.auditIssues}</span>
            <span className="text-3xl font-semibold text-primary">
              {loading || report == null ? '—' : report.issues}
            </span>
          </div>
          <div className="flex flex-col gap-1 rounded-lg border border-border-default bg-surface-2 p-4">
            <span className="text-xs text-tertiary">{S.auditGeneratedAt}</span>
            <span className="text-sm font-medium text-secondary">
              {loading || report == null ? '—' : report.generated_at}
            </span>
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border border-border-default">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-default bg-surface-2 text-left text-xs text-tertiary">
                <th className="px-3 py-2 font-medium">{S.auditStatusCol}</th>
                <th className="px-3 py-2 font-medium">{S.auditCheckCol}</th>
                <th className="px-3 py-2 text-right font-medium">{S.auditCountCol}</th>
                <th className="px-3 py-2 font-medium">{S.auditDetailCol}</th>
              </tr>
            </thead>
            <tbody>
              {(report?.checks ?? []).map((ck) => {
                const m = sevMeta(ck.severity)
                return (
                  <tr key={ck.id} className="border-b border-border-default last:border-0">
                    <td className="px-3 py-2">
                      <span className={`inline-flex items-center gap-1.5 ${m.cls}`}>
                        {m.icon}
                        {m.label}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-primary">{ck.label}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-secondary">{ck.count}</td>
                    <td className="px-3 py-2 text-tertiary">{ck.detail}</td>
                  </tr>
                )
              })}
              {!loading && (report?.checks?.length ?? 0) === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-tertiary">
                    {S.loadFailed}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}
